import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function ImageCropModal({ file, aspectRatio = 1, onClose, onCrop }) {
    const [src, setSrc] = useState(null);
    const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
    
    // Pan and Zoom State
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });

    const imageRef = useRef(null);

    // Hardcode cropper container width for predictable math
    const Cw = 300;
    const Ch = 300 / aspectRatio;
    const QUALITY_MULTIPLIER = 3; // Exports at 3x resolution for crispness

    useEffect(() => {
        if (file) {
            const url = URL.createObjectURL(file);
            setSrc(url);
            setScale(1);
            setPosition({ x: 0, y: 0 });
            return () => URL.revokeObjectURL(url);
        }
    }, [file]);

    const handleImageLoad = (e) => {
        setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });
    };

    // Calculate base rendering dimensions to mimic "object-fit: cover"
    let baseW = Cw;
    let baseH = Ch;
    if (imgSize.w > 0 && imgSize.h > 0) {
        const containerAspect = Cw / Ch;
        const imgAspect = imgSize.w / imgSize.h;
        if (imgAspect > containerAspect) {
            baseH = Ch;
            baseW = Ch * imgAspect;
        } else {
            baseW = Cw;
            baseH = Cw / imgAspect;
        }
    }

    // --- Mouse & Touch Event Handlers for Panning ---
    const handleDragStart = (clientX, clientY) => {
        setDragging(true);
        setStartPos({ x: clientX - position.x, y: clientY - position.y });
    };

    const handleDragMove = (clientX, clientY) => {
        if (!dragging) return;
        setPosition({ x: clientX - startPos.x, y: clientY - startPos.y });
    };

    const handleDragEnd = () => setDragging(false);

    // Desktop Mouse Events
    const onMouseDown = (e) => handleDragStart(e.clientX, e.clientY);
    const onMouseMove = (e) => handleDragMove(e.clientX, e.clientY);
    
    // Mobile Touch Events
    const onTouchStart = (e) => {
        if (e.touches.length === 1) handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchMove = (e) => {
        if (e.touches.length === 1) {
            // Prevent scrolling while panning
            e.preventDefault();
            handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
        }
    };

    // --- Cropping Execution ---
    const handleApplyCrop = () => {
        const canvas = document.createElement('canvas');
        canvas.width = Cw * QUALITY_MULTIPLIER;
        canvas.height = Ch * QUALITY_MULTIPLIER;
        const ctx = canvas.getContext('2d');

        // Fill background with white in case image is panned out of bounds
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Apply transformations identical to CSS rendering
        ctx.scale(QUALITY_MULTIPLIER, QUALITY_MULTIPLIER);
        ctx.translate(Cw / 2, Ch / 2);
        ctx.translate(position.x, position.y);
        ctx.scale(scale, scale);

        // Draw the image
        if (imageRef.current) {
            ctx.drawImage(imageRef.current, -baseW / 2, -baseH / 2, baseW, baseH);
        }

        // Export to Blob and send back as a File
        canvas.toBlob((blob) => {
            if (blob) {
                const croppedFile = new File([blob], file.name || 'cropped-image.jpg', { type: 'image/jpeg' });
                onCrop(croppedFile);
            }
        }, 'image/jpeg', 0.95);
    };

    if (!src) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black/80 z-[999] flex items-center justify-center p-4 animate-fade-in touch-none">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-gray-800 text-lg">Crop Image</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition">✕</button>
                </div>

                <div className="p-6 flex flex-col items-center bg-gray-100">
                    {/* The Crop Window */}
                    <div 
                        className="bg-gray-300 relative overflow-hidden shadow-inner ring-4 ring-white"
                        style={{ width: Cw, height: Ch }}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={handleDragEnd}
                        onMouseLeave={handleDragEnd}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={handleDragEnd}
                    >
                        {/* Overlay Grid lines for rule of thirds */}
                        <div className="absolute inset-0 pointer-events-none z-10 opacity-30 flex flex-col justify-between">
                            <div className="border-b border-white w-full h-1/3 border-dashed"></div>
                            <div className="border-b border-white w-full h-1/3 border-dashed"></div>
                        </div>
                        <div className="absolute inset-0 pointer-events-none z-10 opacity-30 flex justify-between">
                            <div className="border-r border-white h-full w-1/3 border-dashed"></div>
                            <div className="border-r border-white h-full w-1/3 border-dashed"></div>
                        </div>

                        {/* The Draggable Image */}
                        <img
                            ref={imageRef}
                            src={src}
                            onLoad={handleImageLoad}
                            alt="Crop preview"
                            draggable={false}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: baseW,
                                height: baseH,
                                transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                                transformOrigin: 'center center',
                                cursor: dragging ? 'grabbing' : 'grab',
                                maxWidth: 'none' // Prevent tailwind default interference
                            }}
                        />
                    </div>

                    {/* Zoom Slider */}
                    <div className="w-full mt-8 flex items-center gap-3 px-2">
                        <span className="text-gray-500 text-sm font-bold">🔍</span>
                        <input 
                            type="range" 
                            min="1" 
                            max="3" 
                            step="0.05" 
                            value={scale} 
                            onChange={(e) => setScale(Number(e.target.value))}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pink-600"
                        />
                        <span className="text-gray-500 text-lg font-bold">🔍</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-3 text-center">Drag image to position, use slider to zoom.</p>
                </div>

                <div className="p-4 border-t border-gray-100 flex gap-3 bg-white">
                    <button onClick={onClose} className="flex-1 py-2.5 rounded-lg font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition">
                        Cancel
                    </button>
                    <button onClick={handleApplyCrop} className="flex-1 py-2.5 rounded-lg font-bold text-white bg-pink-600 hover:bg-pink-700 transition shadow-md">
                        Apply Crop
                    </button>
                </div>
            </div>
        </div>
    );
}